import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { SnackbarContainer } from 'components/standalone';
import { extractErrorMessage } from 'utils';
import { PermissionsProvider } from 'providers/permissionsProvider';
import {
  CAN_TOGGLE_SUBDIVISION_BUMPOUT_TIMESHEET_FLOW,
  CAN_VIEW_SUBDIVISION_GEOFENCING_THRESHOLD,
  CAN_EDIT_SUBDIVISION_GEOFENCING_THRESHOLD,
} from 'providers/permissions';
import { SubDivisionContainer, SubDivisionFormValues } from '../containers';
import { SubDivisionForm } from '../components/SubDivisionForm';
import { mapCostFields } from '../config/costStructureFields';

const SubDivisionsCreateInner = () => {
  const classes = useStyles();
  const history = useHistory();
  const { notify } = SnackbarContainer.useContainer();
  const { hasPermission } = PermissionsProvider.useContainer();
  const {
    formLoading,
    createSubdivision,
    selectedRegion,
    regionLoading,
    loadRegion,
  } = SubDivisionContainer.useContainer();

  const handleRegionChange = useCallback(
    async (regionId: string) => {
      if (!regionId) return;
      // Load region data for phaseStructureType
      await loadRegion(regionId);
    },
    [loadRegion],
  );

  const handleSubmit = useCallback(
    async (values: SubDivisionFormValues) => {
      try {
        const createData: Record<string, any> = {
          name: values.name,
          address1: values.address1,
          ...(values.address2 && { address2: values.address2 }),
          city: values.city,
          state: values.state,
          zip: values.zip,
          builderId: values.builderId,
          regionId: values.regionId,
          active: values.active,
          reqBumpoutTimesheet: values.reqBumpoutTimesheet,
          warrantyStatus: values.warrantyStatus,
          phaseStructureType: values.phaseStructureType,
          geoDistanceTolerance: values.geoDistanceTolerance,
          overrideRegionCostPerBoard: values.overrideRegionCostPerBoard,
          allowPayoutAmountEditing: values.allowPayoutAmountEditing,
          overrideType: values.overrideRegionCostPerBoard
            ? values.overrideType
            : null,
          // Always send all cost fields (null when not set)
          ...mapCostFields(values),
        };

        // Create the subdivision (no supervisor handling in create mode)
        const result = await createSubdivision(createData);

        notify('Subdivision created');
        history.push(`/subdivision/${result.id}`);
      } catch (error) {
        notify(
          extractErrorMessage(error, 'Error while creating the subdivision'),
          'error',
        );
      }
    },
    [createSubdivision, history, notify],
  );

  const handleGoBack = useCallback(() => {
    history.push('/subdivision');
  }, [history]);

  return (
    <Box className={classes.createBox}>
      <Box className={classes.formContainer}>
        <SubDivisionForm
          mode="create"
          onSubmit={handleSubmit}
          loading={formLoading}
          onGoBack={handleGoBack}
          selectedRegion={selectedRegion}
          regionLoading={regionLoading}
          onRegionChange={handleRegionChange}
          canToggleBumpoutTimesheet={hasPermission(
            CAN_TOGGLE_SUBDIVISION_BUMPOUT_TIMESHEET_FLOW,
          )}
          canViewGeofencingThreshold={hasPermission(
            CAN_VIEW_SUBDIVISION_GEOFENCING_THRESHOLD,
          )}
          canEditGeofencingThreshold={hasPermission(
            CAN_EDIT_SUBDIVISION_GEOFENCING_THRESHOLD,
          )}
        />
      </Box>
    </Box>
  );
};

export const SubDivisionsCreate = () => {
  return (
    <SubDivisionContainer.Provider>
      <SubDivisionsCreateInner />
    </SubDivisionContainer.Provider>
  );
};

const useStyles = makeStyles(theme => ({
  createBox: {
    maxWidth: '1500px',
  },
  formContainer: {
    position: 'relative',
    boxShadow: theme.shadows[2],
    backgroundColor: theme.palette.background.paper,
  },
}));

export default SubDivisionsCreate;
